/**
 * Files dragged out of the File Manager carry their URL (`application/x-genie-file`)
 * and, when known, their identity (`application/x-genie-file-meta`). File nodes
 * read them so a library file can be dropped straight into an existing node.
 */
export const GENIE_FILE_TYPE = 'application/x-genie-file'
export const GENIE_FILE_META_TYPE = 'application/x-genie-file-meta'

export interface LibraryFileDrop {
  uuid: string
  filename: string
  contentType: string
  publicUrl: string
}

const EXTENSION_TYPES: Record<string, string> = {
  png: 'image/png', jpg: 'image/jpeg', jpeg: 'image/jpeg', webp: 'image/webp', gif: 'image/gif', svg: 'image/svg+xml',
  mp4: 'video/mp4', webm: 'video/webm', mov: 'video/quicktime',
  mp3: 'audio/mpeg', wav: 'audio/wav', ogg: 'audio/ogg', m4a: 'audio/mp4', aac: 'audio/aac', flac: 'audio/flac',
}

export function readLibraryFileDrop(dataTransfer: Pick<DataTransfer, 'getData'>): LibraryFileDrop | null {
  const publicUrl = dataTransfer.getData(GENIE_FILE_TYPE)
  if (!publicUrl) return null
  let meta: Partial<LibraryFileDrop> = {}
  try { meta = JSON.parse(dataTransfer.getData(GENIE_FILE_META_TYPE) || '{}') as Partial<LibraryFileDrop> } catch { meta = {} }
  const filename = meta.filename || decodeURIComponent(publicUrl.split('?')[0].split('/').pop() || 'file')
  const extension = filename.split('.').pop()?.toLowerCase() ?? ''
  return {
    uuid: meta.uuid ?? '',
    filename,
    contentType: meta.contentType || EXTENSION_TYPES[extension] || '',
    publicUrl,
  }
}

export type LibraryFileNodeKind = 'image' | 'video' | 'audio'

/**
 * The file node that can show a library file, or null when no canvas node can
 * (3D models, documents). The MIME type wins; the extension covers files
 * stored without one.
 */
export function libraryFileNodeKind(contentType: string | null | undefined, filename: string): LibraryFileNodeKind | null {
  const type = contentType || EXTENSION_TYPES[filename.split('.').pop()?.toLowerCase() ?? ''] || ''
  if (type.startsWith('image/')) return 'image'
  if (type.startsWith('video/')) return 'video'
  if (type.startsWith('audio/')) return 'audio'
  return null
}
